/* eslint-disable @typescript-eslint/no-unused-vars */
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNextStep } from "@/components/NextStepProvider";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import HostedTournamentCard from "@/components/HostedTournamentCard";
import { DefaultButton } from "@/components/DefaultButton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { WithPullToRefresh } from "@/components/ui/pull-to-refresh";
import { useUserHostedTournamentList, useStats } from "@/lib/hooks/useApi";
import type { Tournament } from "@/lib/types/api";
import { Input } from "@/components/ui/input";
import { useSolanaPrice } from "@/lib/hooks/useSolanaPrice";
import { Spinner } from "@/components/ui/spinner";
import { usePrivy } from "@privy-io/react-auth";
import { toast } from "sonner";
import { generateTextShadow } from "@/lib/utils";
import useUserAgent from "@/lib/hooks/useUserAgent";
import { LoginGate } from "@/components/LoginGate";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { HowItWorkButton } from "@/components/ui/HowItWorkButton";
import ResourceBar from "@/components/ResourceBar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { InfoIcon } from "lucide-react";
import { t } from "@/lib/i18n";
import { PasswordDialog } from "@/components/PasswordDialog";
import { CreateModeDialog } from "@/components/CreateModeDialog";

function HostedTournament() {
  const router = useRouter();
  const { user } = usePrivy();
  const { isMobile } = useUserAgent();
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedBetRange, setAppliedBetRange] = useState<
    [number, number] | null
  >(null);
  const [showMine, setShowMine] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "all" | "waiting" | "live" | "cancelled"
  >("all");
  const [passwordProtectedTournament, setPasswordProtectedTournament] =
    useState<Tournament | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { data: solPrice } = useSolanaPrice();

  const walletAddress = user?.wallet?.address ?? "";
  const { data: statsData } = useStats(walletAddress);
  const gameWalletAddress = statsData?.embeddedWallet;
  const userId = statsData?.userId;

  useEffect(() => {
    const handler = setTimeout(() => {
      const trimmed = searchTerm.trim();
      setAppliedSearch(trimmed.length >= 2 ? trimmed : "");
    }, 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    if (!walletAddress && showMine) {
      setShowMine(false);
    }
  }, [walletAddress, showMine]);

  const filterBy = showMine
    ? "me"
    : appliedBetRange
      ? "bet_amount"
      : statusFilter !== "all"
        ? statusFilter
        : undefined;

  const {
    data,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isError,
    refetch,
  } = useUserHostedTournamentList({
    searchBy: appliedSearch || undefined,
    filterBy,
    min: !showMine && appliedBetRange ? appliedBetRange[0] : undefined,
    max: !showMine && appliedBetRange ? appliedBetRange[1] : undefined,
    userId: showMine ? userId : undefined,
  });

  const handlePasswordProtectedClick = useCallback(
    (tournament: Tournament) => {
      const isOwner =
        gameWalletAddress &&
        tournament.ownerProfile?.userPubkey?.toLowerCase() ===
          gameWalletAddress.toLowerCase();
      if (isOwner) {
        const target =
          tournament.gameMode === "championship"
            ? "/private-championship/$tournamentId"
            : "/hosted-tournament/$tournamentId";
        router.navigate({
          to: target,
          params: { tournamentId: tournament.tournamentId },
        });
      } else {
        setPasswordProtectedTournament(tournament);
      }
    },
    [gameWalletAddress, router],
  );

  const handlePasswordSuccess = useCallback(() => {
    const tournament = passwordProtectedTournament;
    if (!tournament) return;
    setPasswordProtectedTournament(null);
    const target =
      tournament.gameMode === "championship"
        ? "/private-championship/$tournamentId"
        : "/hosted-tournament/$tournamentId";
    router.navigate({
      to: target,
      params: { tournamentId: tournament.tournamentId },
    });
  }, [passwordProtectedTournament, router]);

  const handlePasswordBack = useCallback(() => {
    setPasswordProtectedTournament(null);
  }, []);

  const { startNextStep, closeNextStep } = useNextStep();

  const handleStartAlphaDomeTour = useCallback(() => {
    try {
      closeNextStep();
      // eslint-disable-next-line no-empty
    } catch {}
    startNextStep("alphaDomeTour");
  }, [closeNextStep, startNextStep]);

  const handleToggleMine = () => {
    if (!walletAddress) {
      toast.error(t("hostedTournament.error"), {
        description: t("hostedTournament.connectWalletToFilter"),
      });
      return;
    }
    setShowMine((prev) => !prev);
  };

  const tournaments = useMemo(
    () =>
      data?.pages.flatMap((page) => page.tournaments) ?? ([] as Tournament[]),
    [data],
  );

  // No client-side filtering needed - API handles all filtering via filterBy
  const filteredTournaments = tournaments;

  const content = (
    <div className="space-y-3">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-black/80">
          <p className="text-sm">{t("hostedTournament.loadingTournaments")}</p>
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-red-400">
          <p className="text-sm">
            {t("hostedTournament.unableToLoadTournaments")}
          </p>
        </div>
      ) : filteredTournaments.length ? (
        <>
          {filteredTournaments.map((tournament) => (
            <div key={tournament.tournamentId} className="w-full">
              <HostedTournamentCard
                tournament={tournament}
                solPrice={solPrice}
                onPasswordProtectedClick={handlePasswordProtectedClick}
                currentUserAddress={gameWalletAddress}
              />
            </div>
          ))}
          {hasNextPage && (
            <div className="flex justify-center pt-2">
              <DefaultButton
                variant="default"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="uppercase text-sm font-black"
              >
                {isFetchingNextPage
                  ? t("hostedTournament.loading")
                  : t("hostedTournament.loadMore")}
              </DefaultButton>
            </div>
          )}
          {isFetchingNextPage && (
            <div className="py-4 flex justify-center">
              <Spinner className="w-6 h-6" />
            </div>
          )}
          {!hasNextPage && filteredTournaments.length > 0 && (
            <div className="py-4 text-center text-xs text-black/80">
              {t("hostedTournament.youveReachedTheEnd")}
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-black/80">
          <p className="text-lg font-semibold">
            {t("hostedTournament.noHostedTournamentsFound")}
          </p>
          <p className="text-sm">
            {t("hostedTournament.createNewTournamentToGetStarted")}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex flex-1 flex-col h-full overflow-hidden relative">
      <div
        className="absolute top-0 left-0 right-0 h-[25%] pointer-events-none"
        style={{
          backgroundImage: "url(/assets/list-bg-dome.png)",
          backgroundSize: "cover",
          backgroundPosition: "center 3%",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-[75%] pointer-events-none bg-[#546C74]"
        style={{
          backgroundImage: "url(/assets/list-bg-dome-2.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <div className="relative flex flex-col items-center h-full overflow-hidden">
        <FloatingLeftButton
          onClick={() => router.navigate({ to: "/home" })}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />

        <ResourceBar
          showBalance={false}
          className="absolute top-4 left-14 right-4 flex z-40 pl-3"
          rightExtra={
            <HowItWorkButton onClick={handleStartAlphaDomeTour} size="size-8" />
          }
        />

        <div className="w-full max-w-lg flex flex-col h-full relative">
          <div className="flex items-end justify-center h-1/5 py-6 text-center">
            <img
              src="/assets/alpha-dome.png"
              alt={t("hostedTournament.altTexts.alphaDome")}
              className="h-full w-auto object-contain"
              style={{ imageRendering: "pixelated" }}
            />
          </div>
          <div className="flex items-center justify-center px-4">
            <p
              className="text-xs text-white text-center"
              style={{ textShadow: generateTextShadow(1) }}
            >
              {t("hostedTournament.createYourOwnCustomTournaments")}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-black/20 hover:bg-black/30 transition-colors align-middle ml-1">
                    <InfoIcon className="size-3 text-white" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="max-w-xs text-sm text-left">
                  <p className="font-semibold mb-1">
                    {t("hostedTournament.proTip")}
                  </p>
                  <p>{t("hostedTournament.tryOutYourSignalCalls")}</p>
                </PopoverContent>
              </Popover>
            </p>
          </div>

          <div className="flex-1 flex flex-col overflow-hidden p-2 pt-6 gap-4">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex gap-2 items-center flex-wrap">
                  {/* <HostedTournamentFilters
                    appliedBetRange={appliedBetRange}
                    onApply={(value) => setAppliedBetRange(value)}
                  /> */}
                  <Select
                    value={showMine ? "joined" : statusFilter}
                    onValueChange={(value) => {
                      if (value === "joined") {
                        if (!showMine) {
                          setShowMine(true);
                        }
                      } else {
                        setStatusFilter(
                          value as "all" | "waiting" | "live" | "cancelled",
                        );
                        if (showMine) {
                          setShowMine(false);
                        }
                      }
                    }}
                  >
                    <SelectTrigger
                      size="sm"
                      layout="justify-between"
                      className="h-8 min-w-[140px] rounded-lg!"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        {t("hostedTournament.all")}
                      </SelectItem>
                      <SelectItem value="joined">
                        {t("hostedTournament.joined")}
                      </SelectItem>
                      <SelectItem value="waiting">
                        {t("hostedTournament.waiting")}
                      </SelectItem>
                      <SelectItem value="live">
                        {t("hostedTournament.live")}
                      </SelectItem>
                      <SelectItem value="cancelled">
                        {t("hostedTournament.cancelled")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div data-step="alpha-dome-create">
                  <LoginGate
                    unauthenticatedLabel={
                      <div className="flex items-center justify-center gap-1">
                        <svg
                          className="size-2 group-hover:fill-[#5F564F]! fill-[#A18778] -mt-0.5"
                          viewBox="0 0 47 46"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 5V17H0V29H18V46H30V29H47V17H30V5H18Z"
                            fill="currentColor"
                          />
                          <path d="M18 5H30V0H18V5Z" fill="currentColor" />
                        </svg>
                        <span className="mt-0.5">
                          {t("hostedTournament.create")}
                        </span>
                      </div>
                    }
                  >
                    <DefaultButton
                      onClick={() => setShowCreateDialog(true)}
                      className="uppercase h-8"
                    >
                      <div className="flex items-center justify-center gap-1">
                        <svg
                          className="size-2 group-hover:fill-[#5F564F]! fill-[#A18778] -mt-0.5"
                          viewBox="0 0 47 46"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 5V17H0V29H18V46H30V29H47V17H30V5H18Z"
                            fill="currentColor"
                          />
                          <path d="M18 5H30V0H18V5Z" fill="currentColor" />
                        </svg>
                        <span className="mt-0.5">
                          {t("hostedTournament.create")}
                        </span>
                      </div>
                    </DefaultButton>
                  </LoginGate>
                </div>
              </div>
              <Input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t(
                  "hostedTournament.searchTournamentsByNameOrCreator",
                )}
                className="text-base font-semibold text-[#172143] placeholder:text-white/70"
              />
            </div>

            <div
              className="flex-1 flex flex-col overflow-hidden"
              data-step="alpha-dome-list"
            >
              {isMobile ? (
                <WithPullToRefresh
                  refetch={refetch}
                  queryKey={["userHostedTournamentList", appliedSearch]}
                  className="flex-1 flex flex-col"
                >
                  <ScrollArea className="flex-1 min-h-0 pr-1" type="scroll">
                    {content}
                  </ScrollArea>
                </WithPullToRefresh>
              ) : (
                <ScrollArea className="flex-1 min-h-0 pr-1" type="scroll">
                  {content}
                </ScrollArea>
              )}
            </div>
          </div>
        </div>
      </div>

      <CreateModeDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />

      <PasswordDialog
        open={Boolean(passwordProtectedTournament)}
        tournamentId={passwordProtectedTournament?.tournamentId ?? ""}
        tournamentName={passwordProtectedTournament?.tournamentName}
        onSuccess={handlePasswordSuccess}
        onBack={handlePasswordBack}
      />
    </div>
  );
}

export const Route = createFileRoute("/(game)/hosted-tournament/")({
  component: HostedTournament,
});
